import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import ConsentBanner from "@/components/ConsentBanner";
import { LangProvider } from "@/lib/i18n";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import Intake from "@/pages/Intake";
import Faq from "@/pages/Faq";
import Admin from "@/pages/Admin";
import Articles from "@/pages/Articles";
import ArticleDetail from "@/pages/ArticleDetail";
import { Component, createElement } from "react";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(err) {
    console.error("Render error:", err);
  }
  render() {
    if (this.state.hasError) {
      return createElement(
        "div",
        { className: "flex min-h-screen items-center justify-center bg-background" },
        createElement("p", { className: "font-display text-2xl text-sage-700" }, "Something went wrong. Please refresh the page.")
      );
    }
    return this.props.children;
  }
}

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  useEffect(() => {
    const t = setTimeout(() => {
      let granted = false;
      try {
        granted = localStorage.getItem("stillpoint-ga-consent") === "granted";
      } catch (e) {
        granted = false;
      }
      if (granted && typeof window.gtag === "function") {
        window.gtag("event", "page_view", {
          page_path: window.location.pathname,
          page_title: document.title,
        });
      }
    }, 120);
    return () => clearTimeout(t);
  }, [pathname]);
  return null;
};

function App() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ lerp: 0.1 });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <ErrorBoundary>
      <LangProvider>
        <BrowserRouter>
          <ScrollToTop />
          <div className="flex min-h-screen flex-col">
            <Nav />
            <main className="flex-1">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/services" element={<Services />} />
                <Route path="/intake" element={<Intake />} />
                <Route path="/faq" element={<Faq />} />
                <Route path="/articles" element={<Articles />} />
                <Route path="/articles/:slug" element={<ArticleDetail />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="*" element={<Home />} />
              </Routes>
            </main>
            <Footer />
          </div>
          <Toaster position="bottom-center" />
          <ConsentBanner />
        </BrowserRouter>
      </LangProvider>
    </ErrorBoundary>
  );
}

export default App;
